import { useState, useCallback, useEffect } from "react"
import axios from "axios"
import getTimeZoneDateRange from "../../utils/getTimeZoneDateRange"

// =============================================================================
// CONFIRMATION MODAL
// =============================================================================
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmLabel = "Delete", loading }) => {
  if (!isOpen) return null
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-2">{title}</h2>
        <p className="text-sm text-slate-500 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Processing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const SessionDataManager = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Filters — mirrors GSA tab
  const [dateRange, setDateRange] = useState("today")
  const [customStartDate, setCustomStartDate] = useState("")
  const [customEndDate, setCustomEndDate] = useState("")
  const [siteFilter, setSiteFilter] = useState("all")

  // Data state
  const [sessions, setSessions] = useState([])
  const [summary, setSummary] = useState(null) // { total, dateRange }
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastFetched, setLastFetched] = useState("")

  // Pagination
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 })

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    loading: false,
  })

  // Feedback toast
  const [toast, setToast] = useState(null) // { type: 'success'|'error', message }

  // -------------------------------------------------------------------------
  // Date range helpers — same pattern as AnalyticsPage
  // -------------------------------------------------------------------------
  const getDateParams = () => {
    if (dateRange === "custom" && customStartDate && customEndDate) {
      const r1 = getTimeZoneDateRange(timezone, customStartDate)
      const r2 = getTimeZoneDateRange(timezone, customEndDate)
      return { startDate: r1.startDate, endDate: r2.endDate }
    }
    const r = getTimeZoneDateRange(timezone, dateRange)
    return { startDate: r.startDate, endDate: r.endDate }
  }

  const getAuthHeader = () => {
    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
    if (token) axios.defaults.headers.common["Authorization"] = token
  }

  // -------------------------------------------------------------------------
  // Fetch sessions for the selected range
  // -------------------------------------------------------------------------
  const fetchSessions = useCallback(async (page = 1) => {
    setLoading(true)
    setError(null)
    const { startDate, endDate } = getDateParams()

    try {
      getAuthHeader()
      const response = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/session-data?start_date=${startDate}&end_date=${endDate}&site=${siteFilter}&page=${page}&limit=50`
      )
      const result = response.data?.responseData
      setSessions(result?.sessions || [])
      setSummary(result?.summary || null)
      setPagination({
        page: result?.page || 1,
        limit: result?.limit || 50,
        total: result?.total || 0,
      })
      setLastFetched(new Date().toLocaleString())
    } catch (err) {
      setError(err?.message || "Failed to fetch session data")
    } finally {
      setLoading(false)
    }
  }, [dateRange, customStartDate, customEndDate, siteFilter,getDateParams])

  useEffect(()=>{
    fetchSessions()
  },[dateRange,siteFilter,customEndDate,customStartDate])

  // -------------------------------------------------------------------------
  // Download sessions as CSV
  // -------------------------------------------------------------------------
  const downloadCSV = () => {
    if (!sessions.length) return
    const headers = ["session_id", "created_at", "site", "device", "started_assessment", "viewed_plan", "signed_up"]
    const rows = sessions.map(s => [
      s.session_id || "",
      s.created_at || "",
      s.site || "",
      s.device_type || "",
      s.started_assessment ? "Yes" : "No",
      s.viewed_plan ? "Yes" : "No",
      s.signed_up ? "Yes" : "No",
    ])
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `session-data-${dateRange}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // -------------------------------------------------------------------------
  // Show toast helper
  // -------------------------------------------------------------------------
  const showToast = (type, message) => {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }

  // -------------------------------------------------------------------------
  // Delete a single session
  // -------------------------------------------------------------------------
  const handleDeleteSession = (sessionId) => {
    setConfirmModal({
      open: true,
      title: "Delete Session?",
      message: `This will permanently delete session ${sessionId.substring(0, 12)}... and all associated data. This cannot be undone.`,
      loading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }))
        try {
          getAuthHeader()
          const { startDate, endDate } = getDateParams()
          await axios.delete(
            `${window.ajaxLink}cloud_dashboard/analytics/session-data/${sessionId}?start_date=${startDate}&end_date=${endDate}&site=${siteFilter}`
          )
          showToast("success", `Session ${sessionId.substring(0, 12)}... deleted successfully.`)
          setConfirmModal(prev => ({ ...prev, open: false }))
          fetchSessions(pagination.page)
        } catch (err) {
          showToast("error", err?.message || "Failed to delete session.")
          setConfirmModal(prev => ({ ...prev, loading: false }))
        }
      },
    })
  }

  // -------------------------------------------------------------------------
  // Delete ALL sessions in range
  // -------------------------------------------------------------------------
  const handleDeleteAll = () => {
    const { startDate, endDate } = getDateParams()
    setConfirmModal({
      open: true,
      title: "Delete All Sessions in Range?",
      message: `This will permanently delete all ${pagination.total.toLocaleString()} sessions between ${startDate} and ${endDate}${siteFilter !== "all" ? ` for site: ${siteFilter}` : ""}. This cannot be undone.`,
      loading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }))
        try {
          getAuthHeader()
          const { startDate, endDate } = getDateParams()
          await axios.delete(
            `${window.ajaxLink}cloud_dashboard/analytics/session-data?start_date=${startDate}&end_date=${endDate}&site=${siteFilter}`
          )
          showToast("success", `All sessions in range deleted successfully.`)
          setConfirmModal(prev => ({ ...prev, open: false }))
          setSessions([])
          setSummary(null)
          setPagination({ page: 1, limit: 50, total: 0 })
        } catch (err) {
          showToast("error", err?.message || "Failed to delete sessions.")
          setConfirmModal(prev => ({ ...prev, loading: false }))
        }
      },
    })
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === "success" ? "bg-green-600" : "bg-red-600"}`}>
          {toast.message}
        </div>
      )}

      {/* Filters — same style as GSA tab */}
      <section className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Date Range</label>
          <select
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value)
              if (e.target.value !== "custom") {
                setCustomStartDate("")
                setCustomEndDate("")
              }
            }}
            className="rounded-lg border-slate-300 shadow-sm text-sm px-3 py-2 bg-white border focus:border-teal-500"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="wtd">Week to Date</option>
            <option value="mtd">Month to Date</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="all">All Time</option>
            <option value="custom">Custom Range</option>
          </select>

          {dateRange === "custom" && (
            <>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="rounded-lg border-slate-300 shadow-sm text-sm px-3 py-2 bg-white border focus:border-teal-500"
              />
              <span className="text-slate-400 text-sm">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="rounded-lg border-slate-300 shadow-sm text-sm px-3 py-2 bg-white border focus:border-teal-500"
              />
            </>
          )}

          <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Site</label>
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            className="rounded-lg border-slate-300 shadow-sm text-sm px-3 py-2 bg-white border focus:border-teal-500"
          >
            <option value="all">All Sites</option>
            <option value="startfreept">StartFreePT</option>
            <option value="virdio">Virdio Health</option>
          </select>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          <button
            onClick={() => fetchSessions(1)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Loading..." : "Load Sessions"}
          </button>
          {sessions.length > 0 && (
            <>
              <button
                onClick={downloadCSV}
                className="px-4 py-2 text-sm font-medium bg-slate-100 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-200"
              >
                ↓ Download CSV
              </button>
              <button
                onClick={handleDeleteAll}
                className="px-4 py-2 text-sm font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100"
              >
                🗑 Delete All in Range
              </button>
            </>
          )}
        </div>

        {lastFetched && (
          <p className="text-xs text-slate-400 w-full sm:w-auto sm:ml-0">Last fetched: {lastFetched}</p>
        )}
      </section>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Summary KPI cards — only show after fetch */}
      {summary && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Sessions</p>
            <p className="mt-1 text-2xl font-bold text-slate-800">{pagination.total.toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-1">In selected range</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Started GSA</p>
            <p className="mt-1 text-2xl font-bold text-teal-600">{summary.started_gsa?.toLocaleString() || 0}</p>
            <p className="text-xs text-slate-400 mt-1">Of total sessions</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Signed Up</p>
            <p className="mt-1 text-2xl font-bold text-teal-600">{summary.signed_up?.toLocaleString() || 0}</p>
            <p className="text-xs text-slate-400 mt-1">Converted</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Date Range</p>
            <p className="mt-1 text-sm font-bold text-slate-800">{summary.start_date}</p>
            <p className="text-xs text-slate-400 mt-1">to {summary.end_date}</p>
          </div>
        </section>
      )}

      {/* Sessions table */}
      {sessions.length > 0 && (
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Sessions</h2>
              <p className="text-xs text-slate-400 mt-0.5">Click 🗑 to delete an individual session</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Session ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date / Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Site</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Device</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Started GSA</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Signed Up</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {sessions.map((session, idx) => (
                  <tr key={session.session_id || idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">
                      {session.session_id?.substring(0, 12)}...
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {session.created_at ? new Date(session.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {session.site ? (
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          session.site.toLowerCase().includes("startfreept")
                            ? "bg-teal-100 text-teal-700"
                            : "bg-purple-100 text-purple-700"
                        }`}>
                          {session.site.toLowerCase().includes("startfreept") ? "StartFreePT" : "Virdio"}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 capitalize">
                      {session.device_type?.split("|")[0]?.trim() || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {[session.city, session.country].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {session.started_assessment
                        ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-teal-100 text-teal-700">Yes</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {session.signed_up
                        ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Yes</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDeleteSession(session.session_id)}
                        className="text-red-400 hover:text-red-600 transition-colors text-base"
                        title="Delete this session"
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
            <button
              onClick={() => fetchSessions(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Previous {pagination.limit}
            </button>
            <span className="text-sm text-slate-400">
              {pagination.total === 0
                ? "No results"
                : `Showing ${((pagination.page - 1) * pagination.limit) + 1}–${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total.toLocaleString()}`}
            </span>
            <button
              onClick={() => fetchSessions(pagination.page + 1)}
              disabled={pagination.page * pagination.limit >= pagination.total || loading}
              className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next {pagination.limit} →
            </button>
          </div>
        </section>
      )}

      {/* Empty state */}
      {!loading && sessions.length === 0 && lastFetched && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <p className="text-slate-400 text-sm">No sessions found for the selected range and site.</p>
        </div>
      )}

      {/* Prompt before first load */}
      {!lastFetched && !loading && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <p className="text-slate-400 text-sm">Select a date range and site, then click <strong>Load Sessions</strong> to begin.</p>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        loading={confirmModal.loading}
      />
    </div>
  )
}

export default SessionDataManager